import { useMemo, useState } from "react";

// components
import Loading from "../../components/common/LoaderCommon";
import TableComponent from "../../components/common/TableComponent";
import { ReusableCombobox } from "../../utils/components/ui/combobox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../utils/components/ui/dropdown-menu";

// service
import {
  useGetWriteOffsReport,
  useDownloadWriteOffsReportExcel,
} from "../../service/reportsService";
import { useGetAllBranches } from "../../service/branchService";

// icons
import { X, FileSpreadsheet, Loader2 } from "lucide-react";
import PaginationComponent from "../../components/PaginationComponent";

const writeOffTypeOptions = [
  { label: "Dead Stock", value: "DEAD_STOCK" },
  { label: "Customer Credit", value: "CUSTOMER_CREDIT" },
];

function WriteOffsReport() {
  const [selectedType, setSelectedType] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [branchComboboxOpen, setBranchComboboxOpen] = useState(false);
  const [typeComboboxOpen, setTypeComboboxOpen] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data: branchData } = useGetAllBranches({
    params: { page: 0, size: 100 },
  });

  const branchOptions =
    branchData?.content?.map((branch: any) => ({
      value: branch.branchId.toString(),
      label: branch.branchName,
    })) || [];

  const { data, isLoading } = useGetWriteOffsReport({
    page: currentPage,
    pageSize: pageSize,
    type: selectedType || undefined,
    branchId: selectedBranch ? parseInt(selectedBranch) : undefined,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
  });

  const totalPages = data?.totalPages || 0;
  const tableData = data?.content || [];

  const { mutate: downloadExcel, isPending: isDownloading } =
    useDownloadWriteOffsReportExcel();

  const handleExport = () => {
    downloadExcel({
      type: selectedType || undefined,
      branchId: selectedBranch ? parseInt(selectedBranch) : undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    });
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const columns = useMemo(
    () => [
      { header: "Write Off Number", accessor: "writeOffNumber" },
      { header: "Date", accessor: "writeOffDate" },
      { header: "Type", accessor: "type" },
      { header: "Reason", accessor: "reason" },
      { header: "Qty", accessor: "quantity" },
      { header: "Unit Cost", accessor: "unitCost" },
      { header: "Amount", accessor: "amount" },
      { header: "Status", accessor: "status" },
    ],
    [],
  );

  const isFilterActive =
    !!selectedType || !!selectedBranch || !!fromDate || !!toDate;

  const handleResetFilters = () => {
    setSelectedType("");
    setSelectedBranch("");
    setFromDate("");
    setToDate("");
    setCurrentPage(0);
  };

  return (
    <div className="space-y-4">
      {/* Filter card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-5">
        <div className="flex flex-col gap-4">
          {/* Top Row */}
          <div className="flex gap-4 flex-wrap items-center">
            <div className="min-w-[240px]">
              <ReusableCombobox
                options={writeOffTypeOptions}
                value={selectedType}
                onValueChange={(value) => {
                  setSelectedType(value as string);
                }}
                open={typeComboboxOpen}
                setOpen={setTypeComboboxOpen}
                placeholder="Select Type"
                searchPlaceholder="Search type..."
                emptyText="No types found."
                className="bg-[#F5F8FD] border-[#E5ECF8] rounded-[12px] border font-normal h-11"
              />
            </div>
            <div className="min-w-[240px]">
              <ReusableCombobox
                options={branchOptions}
                value={selectedBranch}
                onValueChange={(value) => {
                  setSelectedBranch(value as string);
                }}
                open={branchComboboxOpen}
                setOpen={setBranchComboboxOpen}
                placeholder="Select Branch"
                searchPlaceholder="Search branch..."
                emptyText="No branches found."
                className="bg-[#F5F8FD] border-[#E5ECF8] rounded-[12px] border font-normal h-11"
              />
            </div>
          </div>

          {/* Bottom Row */}
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div className="flex gap-4 items-center flex-wrap">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  To Date
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
            </div>

            <div className="flex gap-2 items-end">
              {isFilterActive && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] px-5 text-[#52627C] font-medium text-sm flex items-center gap-2 hover:bg-[#F5F8FD] transition-colors"
                >
                  <X className="w-4 h-4" />
                  Reset
                </button>
              )}
              <button
                type="button"
                onClick={handleExport}
                disabled={isDownloading}
                className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-sm font-medium text-white transition-colors hover:bg-[#0E4FDE] disabled:cursor-not-allowed disabled:opacity-50 flex items-center gap-2"
              >
                {isDownloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4" />
                )}
                Export to Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table card */}
      {isLoading ? (
        <Loading />
      ) : (
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] overflow-hidden">
          <div className="flex justify-between items-center border-b border-[#E8EEF9] py-3 px-6">
            <p className="text-sm text-[#52627C]">
              <span className="font-semibold text-[#21314A]">
                {data?.totalElements || 0}
              </span>{" "}
              Write-offs found
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#52627C]">Rows per page:</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="h-8 rounded-[8px] border border-[#E5ECF8] bg-[#F5F8FD] px-3 text-sm text-[#23324D] focus:outline-none hover:bg-white"
                      type="button"
                    >
                      {pageSize}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {[10, 20, 30, 50, 100].map((size) => (
                      <DropdownMenuItem
                        key={size}
                        onSelect={() => handlePageSizeChange(size)}
                      >
                        {size}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
          <TableComponent
            caption="A list of your write-offs."
            columns={columns}
            data={tableData}
          />
        </div>
      )}
    </div>
  );
}

export default WriteOffsReport;
